"use client";

import AppleIcon from "@/app/components/icons/AppleIcon";
import CalendlyIcon from "@/app/components/icons/CalendlyIcon";
import DiscordIcon from "@/app/components/icons/DiscordIcon";
import GoogleIcon from "@/app/components/icons/GoogleIcon";
import MicrosoftIcon from "@/app/components/icons/MicrosoftIcon";
import RedditIcon from "@/app/components/icons/RedditIcon";
import SlackIcon from "@/app/components/icons/SlackIcon";
import SpotifyIcon from "@/app/components/icons/SpotifyIcon";
import TeamsIcon from "@/app/components/icons/TeamsIcon";
import TelegramIcon from "@/app/components/icons/TelegramIcon";
import WhatsAppIcon from "@/app/components/icons/WhatsAppIcon";
import YouTubeIcon from "@/app/components/icons/YouTubeIcon";
import { BsLinkedin } from "react-icons/bs";
import { FaMeta, FaTiktok, FaXTwitter } from "react-icons/fa6";
import type { ReactNode } from "react";

type IntegrationProvider = {
  name: string;
  glow: string;
  surface: string;
  icon: ReactNode;
};

const PROVIDERS: IntegrationProvider[] = [
  {
    name: "Google",
    glow: "rgba(66,133,244,0.24)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(66,133,244,0.12))",
    icon: <GoogleIcon size={22} />,
  },
  {
    name: "Microsoft",
    glow: "rgba(0,164,239,0.24)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(0,164,239,0.12))",
    icon: <MicrosoftIcon size={21} />,
  },
  {
    name: "iCloud",
    glow: "rgba(226,232,240,0.2)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(148,163,184,0.12))",
    icon: <AppleIcon color="#f8fafc" width={18} height={22} />,
  },
  {
    name: "Calendly",
    glow: "rgba(0,107,255,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(0,107,255,0.12))",
    icon: <CalendlyIcon color="#006bff" size={22} />,
  },
  {
    name: "Slack",
    glow: "rgba(224,30,90,0.24)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(224,30,90,0.12))",
    icon: <SlackIcon size={21} />,
  },
  {
    name: "Microsoft Teams",
    glow: "rgba(98,100,167,0.24)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(98,100,167,0.12))",
    icon: <TeamsIcon color="#7b83eb" size={22} />,
  },
  {
    name: "WhatsApp",
    glow: "rgba(37,211,102,0.24)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(37,211,102,0.12))",
    icon: <WhatsAppIcon color="#25d366" size={22} />,
  },
  {
    name: "Telegram",
    glow: "rgba(39,166,229,0.24)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(39,166,229,0.12))",
    icon: <TelegramIcon color="#27a6e5" size={21} />,
  },
  {
    name: "Discord",
    glow: "rgba(88,101,242,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(88,101,242,0.12))",
    icon: <DiscordIcon color="#5865f2" size={21} />,
  },
  {
    name: "YouTube",
    glow: "rgba(255,0,0,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,0,0,0.12))",
    icon: <YouTubeIcon color="#ff0000" size={22} />,
  },
  {
    name: "Reddit",
    glow: "rgba(255,69,0,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,69,0,0.12))",
    icon: <RedditIcon color="#ff4500" size={21} />,
  },
  {
    name: "Spotify",
    glow: "rgba(29,185,84,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(29,185,84,0.12))",
    icon: <SpotifyIcon color="#1db954" width={22} height={22} />,
  },
  {
    name: "LinkedIn",
    glow: "rgba(10,102,194,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(10,102,194,0.12))",
    icon: <BsLinkedin aria-hidden="true" color="#0a66c2" size={20} />,
  },
  {
    name: "X",
    glow: "rgba(248,250,252,0.16)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(148,163,184,0.1))",
    icon: <FaXTwitter aria-hidden="true" color="#f8fafc" size={20} />,
  },
  {
    name: "Facebook & Instagram",
    glow: "rgba(99,102,241,0.22)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(99,102,241,0.12))",
    icon: <FaMeta aria-hidden="true" color="#60a5fa" size={22} />,
  },
  {
    name: "TikTok",
    glow: "rgba(37,244,238,0.2)",
    surface: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(37,244,238,0.1))",
    icon: <FaTiktok aria-hidden="true" color="#f8fafc" size={20} />,
  },
];

/**
 * `overlay` pins the rail across the top of its positioned parent; `inline`
 * keeps it in flow, so a parent narrow enough to wrap it into two rows grows
 * instead of letting the second row cover what follows.
 */
export default function IntegrationProviderRail({ placement = "overlay" }: { placement?: "overlay" | "inline" }) {
  return (
    <section
      aria-label="Supported integration providers"
      className={`pointer-events-auto hidden items-center justify-center sm:flex ${
        placement === "overlay" ? "absolute inset-x-0 top-2 px-6" : "relative px-4 pb-1 pt-2"
      }`}
    >
      <div className="flex max-w-[920px] flex-wrap items-center justify-center gap-2">
        {PROVIDERS.map(({ name, glow, surface, icon }) => (
          <div
            key={name}
            role="img"
            aria-label={name}
            title={name}
            className="group relative flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.07] transition-all duration-300 hover:scale-110 hover:border-white/20"
            style={{
              background: surface,
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 3px rgba(0,0,0,0.3), 0 0 20px ${glow}`,
            }}
          >
            <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 translate-y-1 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
              <div className="whitespace-nowrap rounded-full border border-white/10 bg-[#020813]/92 px-2.5 py-1 text-[9px] font-medium tracking-[0.12em] text-cyan-50/90 shadow-[0_10px_24px_rgba(2,8,19,0.42)] backdrop-blur-md sm:text-[10px]">
                {name}
              </div>
            </div>
            <div
              className="absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{ boxShadow: `0 0 24px ${glow}, 0 0 48px ${glow}` }}
            />
            <div className="relative z-10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              {icon}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
